import * as React from 'react';
import {
  Box,
  Tooltip,
  Typography,
  type SxProps,
  type TooltipProps,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { ReactNode } from 'react';
import { ReactComponent as ToolTipIcon } from '../../../assets/PepWise2.0/InfoIcon.svg';

export type TooltipItem = { title: string; description: string | React.ReactNode, titleSx?: SxProps; };
export type TooltipLabel = string | React.ReactNode | TooltipItem[];

interface InfoTooltipProps {
  label?: TooltipLabel;
  icon?: ReactNode;
  className?: string;
  sx?: SxProps;
  listVariant?: React.ComponentProps<typeof Typography>['variant'];
  tooltipProps?: Partial<React.ComponentProps<typeof Tooltip>>;
  hasNoCursor?: boolean;
}

const isTooltipItemArray = (value: unknown): value is TooltipItem[] =>
  Array.isArray(value) && value.every((item) => typeof item?.title === 'string');

export const InfoTooltip = React.memo(function InfoTooltip({
  label,
  icon,
  className,
  sx,
  listVariant = 'bodyText',
  tooltipProps,
  hasNoCursor = false,
}: InfoTooltipProps) {
  const theme = useTheme();
  const colors = theme.palette;
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const tooltipContent = React.useMemo<React.ReactNode>(() => {
    if (!label) return null;

    if (React.isValidElement(label)) {
      return label;
    }

    const resolvedLabel = typeof label === 'function' ? (label as () => React.ReactNode)() : label;

    if (React.isValidElement(resolvedLabel)) {
      return resolvedLabel;
    }

    if (isTooltipItemArray(resolvedLabel)) {
      return (
        <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
          {resolvedLabel.map((item, index) => (
            <Box
              component="li"
              key={`${item.title}-${index}`}
              sx={{
                p: 1,
                bgcolor: colors.tooltip.background.primary,
              }}
            >
              <Typography variant={listVariant} component="div">
                <Box component="strong" sx={{ color: colors.tooltip.text.title,...item.titleSx, }}>
                  {item.title}
                </Box>
                <Box sx={{ color: colors.tooltip.text.primary }}>{item.description}</Box>
              </Typography>
            </Box>
          ))}
        </Box>
      );
    }

    return (
      <Box sx={{ p: 1, bgcolor: colors.tooltip.background.primary }}>
        <Typography variant={listVariant} sx={{ color: colors.tooltip.text.title }}>
          {resolvedLabel as React.ReactNode}
        </Typography>
      </Box>
    );
  }, [label, listVariant, colors]);

  if (!tooltipContent) return null;

  const placement = tooltipProps?.placement ?? (isMobile ? 'bottom-start' : 'bottom');

  const trigger = (
    <Box
      component="span"
      tabIndex={0}
      role="button"
      aria-label="More information"
      sx={{
        ml: '8px',
        cursor: hasNoCursor ? 'default' : 'help',
        lineHeight: 0,
        display: 'inline-flex',
        alignItems: 'center',
        outline: 'none',
      }}
    >
      {icon || <ToolTipIcon height="18px" width="18px" />}
    </Box>
  );

  return (
    <Box
      className={className}
      sx={{
        display: 'flex',
        alignItems: 'center',
        transition: 'opacity 0.3s ease-in-out',
        ...sx,
      }}
    >
      <Tooltip
        title={tooltipContent}
        describeChild
        arrow={true}
        enterDelay={tooltipProps?.enterDelay ?? 300}
        enterNextDelay={tooltipProps?.enterNextDelay ?? 300}
        leaveDelay={tooltipProps?.leaveDelay ?? 100}
        placement={placement}
        {...tooltipProps}
        slotProps={{
          ...tooltipProps?.slotProps,
          tooltip: {
            ...tooltipProps?.slotProps?.tooltip,
            sx: {
              p: 0,
              maxWidth: { xs: 260, sm: 320, md: 360 },
              bgcolor: colors.tooltip.background.primary,
              color: colors.tooltip.text.primary,
              borderRadius: '12px',
              overflow: 'hidden',
              boxShadow: `
                -6px 6px 12px rgba(0, 0, 0, 0.18),
                -2px 10px 8px rgba(0, 0, 0, 0.18)
              `,
              ...(tooltipProps?.slotProps?.tooltip?.sx as any),
            },
          },
          popper: {
            ...tooltipProps?.slotProps?.popper,
            modifiers: [
              {
                name: 'flip',
                enabled: true,
                options: {
                  fallbackPlacements: isMobile
                    ? ['top-start', 'bottom', 'top']
                    : ['bottom', 'right', 'left'],
                },
              },
              {
                name: 'preventOverflow',
                enabled: true,
                options: {
                  padding: 12,
                  altAxis: true,
                  tether: true,
                },
              },
              ...(((tooltipProps?.slotProps?.popper as any)?.modifiers ?? []) as any[]),
            ],
          },
        }}
      >
        {trigger}
      </Tooltip>
    </Box>
  );
});
